import React, { useState, useEffect, useRef } from 'react';
import './Template.css';
import axios from 'axios';
import bin from '../Assets/Icons/bin.png';
import { toast } from 'react-toastify'
import { useAuth } from '../Auth/AuthContext'

export default function Template() {
    const { currentUser } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [venues, setVenues] = useState([]);
    const [open, setOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingTemplateId, setEditingTemplateId] = useState(null);
    const [selectedDay, setSelectedDay] = useState("")
    const [eventType, setEventType] = useState("")
    const [venueId, setVenueId] = useState("")
    const [venueName, setVenueName] = useState("")
    const [setTimes, setSetTimes] = useState([])
    const [saving, setSaving] = useState(false)
    const [artistFee, setArtistFee] = useState(null)
    const [venueFee, setVenueFee] = useState(null)
    const [notes, setNotes] = useState("")
    const [newFromTime, setNewFromTime] = useState("")
    const [newToTime, setNewToTime] = useState("")
    const isLoadingTemplateRef = useRef(false)


    useEffect(() => {
        if (currentUser?.accessToken) {
            getTemplates()
            getVenues()
        }
    }, [currentUser])

    useEffect(() => {
        if (venueId && !isEditMode && venues.length > 0 && !isLoadingTemplateRef.current) {
            // Always repopulate from venue when adding new template (not when editing)
            let venue = venues.find((ven) => ven._id === venueId)
            if (venue) {
                setVenueName(venue.venueName)
                // Always repopulate these fields when venue changes in add mode
                setSetTimes(venue.setTimes || [])
                setArtistFee(venue.artistFee || null)
                setVenueFee(venue.venueFee || null)
                setNotes(venue.venueReq || "")
            }
        }
    }, [venueId, isEditMode, venues])

    const getVenues = () => {
        if (!currentUser?.accessToken) return;
        axios.get(`${process.env.REACT_APP_API}/venue/get-venues`, { headers: { 'AuthToken': currentUser.accessToken } })
            .then((res) => {
                setVenues(res.data)
                console.log(res.data)
            })
            .catch((e) => {
                console.log(e)
            })
    }

    const getTemplates = () => {
        if (!currentUser?.accessToken) return;
        axios.get(`${process.env.REACT_APP_API}/template/get-templates`, { headers: { 'AuthToken': currentUser.accessToken } })
            .then((res) => {
                setTemplates(res.data)
                console.log(res.data)
            })
            .catch((e) => {
                console.log(e)
            })
    }

    // Days array reordered for Monday-Sunday display
    const days = [
        { label: 'Monday', index: 1 },
        { label: 'Tuesday', index: 2 },
        { label: 'Wednesday', index: 3 },
        { label: 'Thursday', index: 4 },
        { label: 'Friday', index: 5 },
        { label: 'Saturday', index: 6 },
        { label: 'Sunday', index: 0 },
    ];

    const getDayLabel = (dayIndex) => {
        const day = days.find(d => d.index === dayIndex);
        return day ? day.label : '';
    };

    const resetForm = () => {
        setEventType("")
        setVenueId("")
        setVenueName("")
        setSetTimes([])
        setVenueFee(null)
        setArtistFee(null)
        setNotes("")
        setSelectedDay("")
        setNewFromTime("")
        setNewToTime("")
        setIsEditMode(false)
        setEditingTemplateId(null)
        isLoadingTemplateRef.current = false
    }

    const handleSave = () => {
        setSaving(true)
        let payload = {
            day: selectedDay,
            eventType,
            venueName,
            venueId,
            setTimes,
            artistFee,
            venueFee,
            notes
        }
        if (!venueId || !eventType || setTimes.length < 1 || !artistFee || !venueFee) {
            toast.error("Please add all fields")
            setSaving(false)
        } else {
            const endpoint = isEditMode
                ? `${process.env.REACT_APP_API}/template/update-template`
                : `${process.env.REACT_APP_API}/template/add-template`;

            const requestPayload = isEditMode
                ? { id: editingTemplateId, payload }
                : payload;

            axios.post(endpoint, requestPayload, { headers: { 'AuthToken': currentUser.accessToken } })
                .then(() => {
                    toast.success(isEditMode ? "Template updated successfully" : "Template created successfully")
                    setOpen(false)
                    resetForm()
                    getTemplates()
                })
                .catch((e) => {
                    console.log(e)
                    toast.error("Error saving template")
                })
                .finally(() => {
                    setSaving(false)
                })
        }

    }

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete?")) {
            axios.post(`${process.env.REACT_APP_API}/template/delete`, { id }, { headers: { 'AuthToken': currentUser.accessToken } })
                .then(() => {
                    getTemplates()
                })
                .catch((e) => {
                    console.log(e)
                })
        } else {
            return
        }
    }

    const deleteSet = (index) => {
        let newSets = setTimes.filter((_, i) => i !== index)
        setSetTimes(newSets)
    }

    const addSetTime = () => {
        if (!newFromTime || !newToTime) {
            toast.error("Please enter both from and to times")
            return
        }

        // Validate time format (HH:mm)
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
        if (!timeRegex.test(newFromTime) || !timeRegex.test(newToTime)) {
            toast.error("Please enter times in HH:mm format (e.g., 20:00)")
            return
        }

        // Check if from time is before to time
        const fromMinutes = parseInt(newFromTime.split(':')[0]) * 60 + parseInt(newFromTime.split(':')[1])
        const toMinutes = parseInt(newToTime.split(':')[0]) * 60 + parseInt(newToTime.split(':')[1])

        if (fromMinutes >= toMinutes) {
            toast.error("From time must be before to time")
            return
        }

        const newSet = {
            from: newFromTime,
            to: newToTime
        }

        setSetTimes([...setTimes, newSet])
        setNewFromTime("")
        setNewToTime("")
    }

    const handleOpen = (template) => {
        // Set flag to prevent useEffect from interfering
        isLoadingTemplateRef.current = true

        // Set edit mode first
        setIsEditMode(true)
        setEditingTemplateId(template._id)

        // Populate form with template data for editing
        // Handle 0 values correctly (0 is falsy but valid)
        setSelectedDay(template.day)
        setEventType(template.eventType)
        setVenueName(template.venueName)
        setSetTimes(template.setTimes || [])
        setArtistFee(template.artistFee !== undefined && template.artistFee !== null ? template.artistFee : null)
        setVenueFee(template.venueFee !== undefined && template.venueFee !== null ? template.venueFee : null)
        setNotes(template.notes || "")

        // Set venueId last to avoid triggering useEffect
        setVenueId(template.venueId)
        setOpen(true)

        // Reset flag after a brief delay to allow state updates to complete
        setTimeout(() => {
            isLoadingTemplateRef.current = false
        }, 100)
    }

    const handleClose = () => {
        setOpen(false)
        resetForm()
    }


    return (
        <div >
            <table id='template-table'>
                <thead>
                    <tr>
                        {days.map((day) => (
                            <th key={day.index}>{day.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {days.map((day) => (
                            <td key={day.index}>
                                <div className="shift-container">
                                    {/* Filter and display events for the current day */}
                                    {templates
                                        ?.filter((gig) => gig.day === day.index) // Only render events for the current day
                                        .map((gig, idx) => (
                                            <div key={idx} className="gig" onClick={() => handleOpen(gig)}>
                                                <img
                                                    src={bin}
                                                    className="delete-gig"
                                                    alt="Music Gofer delete bar or restaurant live music"
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent triggering edit modal
                                                        handleDelete(gig._id);
                                                    }}
                                                />
                                                <strong>{gig.venueName} ({gig.eventType})</strong>
                                                <p>
                                                    {gig.setTimes[0]?.from} - {gig.setTimes[gig.setTimes.length - 1]?.to}
                                                </p>
                                            </div>
                                        ))}

                                    <button
                                        className="add-button"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent triggering parent click
                                            setIsEditMode(false);
                                            setEditingTemplateId(null);
                                            resetForm();
                                            setSelectedDay(day.index);
                                            setOpen(true);
                                        }}
                                    >
                                        + Add Gig
                                    </button>
                                </div>
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>

            {open &&
                <div className='modal'>
                    <h2>{isEditMode ? "Edit gig template" : "Add gig to template"}</h2>
                    {selectedDay !== "" && (
                        <p style={{ fontSize: '14px', color: '#666', margin: '4px 0 16px 0', fontWeight: 'normal' }}>
                            {getDayLabel(selectedDay)}
                        </p>
                    )}
                    <select
                        className='modal-select'
                        value={venueId}
                        onChange={(e) => setVenueId(e.target.value)}
                        disabled={isEditMode}
                        style={isEditMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                    >
                        <option value="" disabled>Select Venue</option>
                        {venues.map((venue, i) => {
                            return (
                                <option key={i} value={venue._id}>{venue.venueName}</option>
                            )
                        })}
                    </select>
                    {isEditMode && (
                        <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>
                            Venue cannot be changed when editing
                        </p>
                    )}

                    <select className='modal-select' value={eventType} onChange={(e) => setEventType(e.target.value)}>
                        <option value="" disabled>Select Act Type</option>
                        <option value="Solo">Solo</option>
                        <option value="Duo">Duo</option>
                        <option value="DJ">DJ</option>
                        <option value="Dancer">Dancer</option>
                        <option value="Band">Band</option>
                    </select>

                    <div style={{ width: '100%', marginBottom: 10 }}>
                        <label className='modal-label' style={{ marginBottom: 8, display: 'block' }}>Set Times</label>
                        {setTimes.map((timings, i) => {
                            return (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, padding: '8px', backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                                    <p style={{ margin: 0 }}>{timings.from} - {timings.to}</p>
                                    <img src={bin} onClick={() => deleteSet(i)} style={{ height: 20, width: 20, cursor: 'pointer' }} alt='Delete set time' />
                                </div>
                            )
                        })}

                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                            <input
                                type="time"
                                className='modal-input'
                                style={{ flex: 1, margin: 0 }}
                                value={newFromTime}
                                onChange={(e) => setNewFromTime(e.target.value)}
                                placeholder="From"
                            />
                            <span style={{ fontSize: '18px' }}>-</span>
                            <input
                                type="time"
                                className='modal-input'
                                style={{ flex: 1, margin: 0 }}
                                value={newToTime}
                                onChange={(e) => setNewToTime(e.target.value)}
                                placeholder="To"
                            />
                            <button
                                type="button"
                                onClick={addSetTime}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f47fdf',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                Add
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <label className='modal-label'>Artist Fee</label>
                        <input className='modal-input' value={artistFee} onChange={(e) => setArtistFee(e.target.value)} />
                        <label className='modal-label'>Venue Fee</label>
                        <input className='modal-input' value={venueFee} onChange={(e) => setVenueFee(e.target.value)} />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <label className='modal-label'>Notes</label>
                        <textarea style={{ resize: 'none', height: 90, paddingTop: 10 }} className='modal-input' value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>

                    <div>
                        <button className='save-btn' onClick={handleSave} disabled={saving}>{saving ? "Saving..." : isEditMode ? "Update" : "Save"}</button>
                        <button className='close-btn' onClick={handleClose}>Close</button>
                    </div>

                </div>
            }
        </div>
    );
}
